import { type bookingDateRangeQuery } from "../queries";
import { type ResultOf } from "@/graphql";

export type DateRangeData = ResultOf<typeof bookingDateRangeQuery>;

/**
 * Calculates the year range for date picker options based on booking data.
 *
 * @param dateRangeData - GraphQL query result containing earliest and latest booking dates
 * @param currentYear - Current year (defaults to actual current year, but can be overridden for testing)
 * @returns Object with yearsBack and yearsForward properties
 */
export function getYearRange(
  dateRangeData: DateRangeData | undefined,
  currentYear: number = new Date().getFullYear(),
): { yearsBack: number; yearsForward: number } {
  if (!dateRangeData) return { yearsBack: 2, yearsForward: 1 }; // fallback

  const earliestDate = dateRangeData.supplyChainBookings?.edges[0]?.node?.plannedShippingDate;
  const latestDate = dateRangeData.latestBookings?.edges[0]?.node?.plannedShippingDate;

  if (earliestDate && latestDate) {
    const earliestDateObj = new Date(earliestDate);
    const latestDateObj = new Date(latestDate);

    // Check if dates are valid
    if (isNaN(earliestDateObj.getTime()) || isNaN(latestDateObj.getTime())) {
      return { yearsBack: 2, yearsForward: 1 }; // fallback for invalid dates
    }

    const earliestYear = earliestDateObj.getFullYear();
    const latestYear = latestDateObj.getFullYear();

    return {
      yearsBack: Math.max(0, currentYear - earliestYear),
      yearsForward: Math.max(0, latestYear - currentYear),
    };
  }

  return { yearsBack: 2, yearsForward: 1 }; // fallback
}
